import { useMemo, useState } from "react";
import { Link, createRoute, useNavigate } from "@tanstack/react-router";
import { staffRoute } from "./staff";
import {
  useBookSearchQuery,
  useCreateRoomMutation,
  useParseYamlMutation,
  usePreviewMargauxMutation,
  type BookRefInput,
  type BookSearchResult,
  type MargauxPreview,
  type ParsedYamlRoom,
  type SegmentInput,
} from "@/lib/staff/create-mutations";
import styles from "./staff-plotlounge-room-new.module.scss";

type FormMode = "form" | "yaml";

interface SelectedBook {
  ref: BookRefInput;
  display: { title: string; author: string; coverUrl: string | null };
}

interface SegmentDraft {
  title: string;
  description: string;
  startPage: string;
  endPage: string;
}

const EMPTY_SEGMENT: SegmentDraft = {
  title: "",
  description: "",
  startPage: "",
  endPage: "",
};

function emptySegments(): SegmentDraft[] {
  return [
    { ...EMPTY_SEGMENT },
    { ...EMPTY_SEGMENT },
    { ...EMPTY_SEGMENT },
    { ...EMPTY_SEGMENT },
  ];
}

function toIsoDate(localDate: string): string | null {
  if (!localDate) return null;
  const d = new Date(`${localDate}T12:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function fromIsoToInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function buildSegments(drafts: SegmentDraft[]): SegmentInput[] | undefined {
  const allEmpty = drafts.every(
    (d) => !d.title && !d.startPage && !d.endPage && !d.description,
  );
  if (allEmpty) return undefined;
  return drafts.map((d, i) => {
    const startPage = d.startPage ? Number(d.startPage) : undefined;
    const endPage = d.endPage ? Number(d.endPage) : undefined;
    return {
      segmentNumber: i + 1,
      title: d.title,
      description: d.description || undefined,
      startPage,
      endPage,
    };
  });
}

function PlotloungeRoomNew() {
  const navigate = useNavigate();

  const [mode, setMode] = useState<FormMode>("form");
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [maxMembers, setMaxMembers] = useState("15");
  const [hookLine, setHookLine] = useState("");
  const [bookSearch, setBookSearch] = useState("");
  const [selectedBook, setSelectedBook] = useState<SelectedBook | null>(null);
  const [segments, setSegments] = useState<SegmentDraft[]>(emptySegments);
  const [yamlBody, setYamlBody] = useState("");
  const [preview, setPreview] = useState<MargauxPreview | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [yamlImported, setYamlImported] = useState(false);

  const showResults = bookSearch.trim().length >= 2 && !selectedBook;
  const search = useBookSearchQuery(showResults ? bookSearch : "");
  const previewMutation = usePreviewMargauxMutation();
  const parseYaml = useParseYamlMutation();
  const createRoom = useCreateRoomMutation();

  const fieldErrors = useMemo(() => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = "Name is required.";
    if (!selectedBook) errs.book = "Pick a book.";
    if (!startDate) errs.startDate = "Start date is required.";
    if (!endDate) errs.endDate = "End date is required.";
    if (startDate && endDate && new Date(endDate) <= new Date(startDate)) {
      errs.endDate = "End date must be after start date.";
    }
    if (hookLine.length > 280) errs.hookLine = "Hook line must be ≤ 280 chars.";
    return errs;
  }, [name, selectedBook, startDate, endDate, hookLine]);

  const canSubmit =
    Object.keys(fieldErrors).length === 0 && !createRoom.isPending;

  function selectBook(b: BookSearchResult) {
    // /books/search returns external-provider candidates without a DB id, so
    // we always send the create-on-demand spec. The server resolves it via
    // findFirst({ title, author }) and creates the row if it doesn't exist.
    setSelectedBook({
      ref: {
        title: b.title,
        author: b.author,
        description: b.description ?? undefined,
        coverUrl: b.coverUrl ?? undefined,
      },
      display: {
        title: b.title,
        author: b.author,
        coverUrl: b.coverUrl ?? null,
      },
    });
    setBookSearch(`${b.title} — ${b.author}`);
    setPreview(null);
  }

  function clearBook() {
    setSelectedBook(null);
    setBookSearch("");
    setPreview(null);
  }

  function updateSegment(i: number, patch: Partial<SegmentDraft>) {
    setSegments((prev) =>
      prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)),
    );
  }

  async function handlePreviewMargaux() {
    if (!selectedBook) return;
    setFormError(null);
    setPreview(null);
    try {
      const res = await previewMutation.mutateAsync({ book: selectedBook.ref });
      setPreview(res);
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : "Couldn't preview Margaux.",
      );
    }
  }

  async function handleParseYaml() {
    setFormError(null);
    setYamlImported(false);
    try {
      const parsed = await parseYaml.mutateAsync({ yaml: yamlBody });
      applyParsedYaml(parsed);
      setYamlImported(true);
      setMode("form");
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : "YAML failed to parse.",
      );
    }
  }

  function applyParsedYaml(parsed: ParsedYamlRoom) {
    setName(parsed.name);
    setStartDate(fromIsoToInput(parsed.startDate));
    setEndDate(fromIsoToInput(parsed.endDate));
    setMaxMembers(parsed.maxMembers != null ? String(parsed.maxMembers) : "15");
    setHookLine(parsed.hookLine ?? "");

    if ("id" in parsed.book) {
      setSelectedBook({
        ref: { id: parsed.book.id },
        display: {
          title: `Book ${parsed.book.id}`,
          author: "(loaded by id)",
          coverUrl: null,
        },
      });
      setBookSearch(`Book ${parsed.book.id}`);
    } else {
      setSelectedBook({
        ref: {
          title: parsed.book.title,
          author: parsed.book.author,
          description: parsed.book.description,
          coverUrl: parsed.book.coverUrl,
        },
        display: {
          title: parsed.book.title,
          author: parsed.book.author,
          coverUrl: parsed.book.coverUrl ?? null,
        },
      });
      setBookSearch(`${parsed.book.title} — ${parsed.book.author}`);
    }

    if (parsed.segments && parsed.segments.length === 4) {
      setSegments(
        parsed.segments.map((s) => ({
          title: s.title,
          description: s.description ?? "",
          startPage: s.startPage != null ? String(s.startPage) : "",
          endPage: s.endPage != null ? String(s.endPage) : "",
        })),
      );
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    if (!canSubmit || !selectedBook) return;

    const start = toIsoDate(startDate);
    const end = toIsoDate(endDate);
    if (!start || !end) {
      setFormError("Start and end dates must be valid ISO dates.");
      return;
    }

    try {
      const result = await createRoom.mutateAsync({
        name: name.trim(),
        book: selectedBook.ref,
        startDate: start,
        endDate: end,
        maxMembers: maxMembers ? Number(maxMembers) : undefined,
        hookLine: hookLine.trim() || undefined,
        segments: buildSegments(segments),
      });
      navigate({
        to: "/staff/plotlounge-rooms/$id",
        params: { id: result.id },
      });
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : "Failed to create room.",
      );
    }
  }

  return (
    <main className={styles.shell}>
      <aside className={styles.aside}>
        <p className={styles.eyebrow}>New room</p>
        <h2 className={styles.asideTitle}>Set up cohort</h2>
        <ul className={styles.asideNav}>
          <li>
            <a href="#book" className={styles.asideLink}>
              Book
            </a>
          </li>
          <li>
            <a href="#schedule" className={styles.asideLink}>
              Schedule
            </a>
          </li>
          <li>
            <a href="#cohort" className={styles.asideLink}>
              Cohort
            </a>
          </li>
          <li>
            <a href="#margaux" className={styles.asideLink}>
              Margaux
            </a>
          </li>
          <li>
            <a href="#segments" className={styles.asideLink}>
              Segments
            </a>
          </li>
        </ul>

        <div className={styles.modeRow}>
          <span className={styles.eyebrow}>Mode</span>
          <button
            type="button"
            onClick={() => setMode("form")}
            className={`${styles.modeBtn} ${
              mode === "form" ? styles.modeBtnActive : ""
            }`}
          >
            Form
          </button>
          <button
            type="button"
            onClick={() => setMode("yaml")}
            className={`${styles.modeBtn} ${
              mode === "yaml" ? styles.modeBtnActive : ""
            }`}
          >
            Paste YAML
          </button>
        </div>
      </aside>

      <div className={styles.main}>
        <nav className={styles.crumbs} aria-label="Breadcrumb">
          <Link to="/staff/plotlounge-rooms" className={styles.crumbLink}>
            Plotlounge rooms
          </Link>
          <span aria-hidden>/</span>
          <span>New room</span>
        </nav>

        <header className={styles.head}>
          <h1 className={styles.title}>New Plotlounge room</h1>
          <p className={styles.lede}>
            Pick a book, set the schedule, and let Margaux warm up the room. Use
            the YAML mode if you already have a definition you want to import.
          </p>
        </header>

        {formError ? (
          <div className={styles.errorBanner} role="alert">
            {formError}
          </div>
        ) : null}

        {yamlImported ? (
          <div className={styles.successBanner} role="status">
            YAML imported. Review the fields below before submitting.
          </div>
        ) : null}

        {mode === "yaml" ? (
          <section
            className={styles.section}
            id="yaml"
            aria-labelledby="yaml-heading"
          >
            <div className={styles.sectionHead}>
              <span className={styles.sectionLabel}>Import</span>
              <h2 id="yaml-heading" className={styles.sectionTitle}>
                Paste a room YAML
              </h2>
            </div>
            <p className={styles.helperText}>
              Same format as the CLI. The first room in the document is used.
              Validate to load values into the form, then submit.
            </p>
            <textarea
              aria-label="YAML body"
              className={styles.textarea}
              value={yamlBody}
              onChange={(e) => setYamlBody(e.target.value)}
              placeholder={`rooms:\n  - name: Slow Reads\n    book:\n      title: Dune\n      author: Frank Herbert\n    startDate: "2026-06-01"\n`}
            />
            <div className={styles.foot}>
              <button
                type="button"
                className={styles.btn}
                onClick={() => setMode("form")}
              >
                Back to form
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={handleParseYaml}
                disabled={!yamlBody.trim() || parseYaml.isPending}
              >
                {parseYaml.isPending ? "Validating…" : "Validate & import"}
              </button>
            </div>
          </section>
        ) : null}

        {mode === "form" ? (
          <form onSubmit={handleSubmit}>
            <section
              className={styles.section}
              id="book"
              aria-labelledby="book-heading"
            >
              <div className={styles.sectionHead}>
                <span className={styles.sectionLabel}>Step 1</span>
                <h2 id="book-heading" className={styles.sectionTitle}>
                  Book
                </h2>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="bookSearch">
                  Search the catalog
                  <span className={styles.labelHint}>
                    Type a title or author. Pick a result to lock the book in.
                  </span>
                </label>
                <div>
                  <input
                    id="bookSearch"
                    className={styles.input}
                    type="search"
                    placeholder="Search by title or author…"
                    value={bookSearch}
                    onChange={(e) => {
                      setBookSearch(e.target.value);
                      if (selectedBook) setSelectedBook(null);
                    }}
                    autoComplete="off"
                  />
                  {showResults && search.data && search.data.length > 0 ? (
                    <div className={styles.bookSearchResults} role="listbox">
                      {search.data.map((b, i) => (
                        <button
                          key={
                            b.openLibraryId ||
                            b.googleBooksId ||
                            b.isbn ||
                            `${b.title}-${b.author}-${i}`
                          }
                          type="button"
                          role="option"
                          aria-selected="false"
                          className={styles.bookSearchRow}
                          onClick={() => selectBook(b)}
                        >
                          <span className={styles.bookSearchTitle}>
                            {b.title}
                          </span>
                          <span className={styles.bookSearchAuthor}>
                            {b.author}
                            {b.pageCount ? ` · ${b.pageCount}p` : ""}
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {showResults && search.isLoading ? (
                    <p className={styles.helperText}>Searching…</p>
                  ) : null}
                  {fieldErrors.book ? (
                    <p className={styles.fieldError}>{fieldErrors.book}</p>
                  ) : null}
                </div>
              </div>

              {selectedBook ? (
                <div className={styles.field}>
                  <span className={styles.label}>Selected book</span>
                  <div className={styles.bookCard}>
                    {selectedBook.display.coverUrl ? (
                      <img
                        className={styles.bookCover}
                        src={selectedBook.display.coverUrl}
                        alt=""
                      />
                    ) : (
                      <div className={styles.bookCover} aria-hidden />
                    )}
                    <div className={styles.bookMeta}>
                      <span className={styles.bookTitle}>
                        {selectedBook.display.title}
                      </span>
                      <span className={styles.bookAuthor}>
                        {selectedBook.display.author}
                      </span>
                    </div>
                    <button
                      type="button"
                      className={styles.btn}
                      onClick={clearBook}
                    >
                      Change
                    </button>
                  </div>
                </div>
              ) : null}
            </section>

            <section
              className={styles.section}
              id="schedule"
              aria-labelledby="schedule-heading"
            >
              <div className={styles.sectionHead}>
                <span className={styles.sectionLabel}>Step 2</span>
                <h2 id="schedule-heading" className={styles.sectionTitle}>
                  Schedule
                </h2>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="name">
                  Room name
                </label>
                <div>
                  <input
                    id="name"
                    className={styles.input}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Summer Read"
                  />
                  {fieldErrors.name ? (
                    <p className={styles.fieldError}>{fieldErrors.name}</p>
                  ) : null}
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="startDate">
                  Start date
                </label>
                <div>
                  <input
                    id="startDate"
                    type="date"
                    className={styles.input}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                  {fieldErrors.startDate ? (
                    <p className={styles.fieldError}>{fieldErrors.startDate}</p>
                  ) : null}
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="endDate">
                  End date
                  <span className={styles.labelHint}>
                    Defaults are 4 weeks from start.
                  </span>
                </label>
                <div>
                  <input
                    id="endDate"
                    type="date"
                    className={styles.input}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                  {fieldErrors.endDate ? (
                    <p className={styles.fieldError}>{fieldErrors.endDate}</p>
                  ) : null}
                </div>
              </div>
            </section>

            <section
              className={styles.section}
              id="cohort"
              aria-labelledby="cohort-heading"
            >
              <div className={styles.sectionHead}>
                <span className={styles.sectionLabel}>Step 3</span>
                <h2 id="cohort-heading" className={styles.sectionTitle}>
                  Cohort
                </h2>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="maxMembers">
                  Max members
                </label>
                <input
                  id="maxMembers"
                  type="number"
                  min={1}
                  className={styles.input}
                  value={maxMembers}
                  onChange={(e) => setMaxMembers(e.target.value)}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="hookLine">
                  Hook line
                  <span className={styles.labelHint}>
                    280 char max. Leave blank for a Margaux-generated line.
                  </span>
                </label>
                <div>
                  <input
                    id="hookLine"
                    className={styles.input}
                    maxLength={300}
                    value={hookLine}
                    onChange={(e) => setHookLine(e.target.value)}
                    placeholder="A wild ride through dystopia"
                  />
                  {fieldErrors.hookLine ? (
                    <p className={styles.fieldError}>{fieldErrors.hookLine}</p>
                  ) : null}
                </div>
              </div>
            </section>

            <section
              className={styles.section}
              id="margaux"
              aria-labelledby="margaux-heading"
            >
              <div className={styles.sectionHead}>
                <span className={styles.sectionLabel}>Step 4</span>
                <h2 id="margaux-heading" className={styles.sectionTitle}>
                  Margaux preview
                </h2>
              </div>
              <p className={styles.helperText}>
                Generate Margaux's intro and hook line for the selected book
                before committing. Useful for catching awkward AI output.
              </p>
              <div className={styles.field}>
                <span className={styles.label}>Run preview</span>
                <div>
                  <button
                    type="button"
                    className={styles.btn}
                    onClick={handlePreviewMargaux}
                    disabled={!selectedBook || previewMutation.isPending}
                  >
                    {previewMutation.isPending
                      ? "Generating…"
                      : "Preview Margaux"}
                  </button>
                  {!selectedBook ? (
                    <p className={styles.helperText}>
                      Pick a book first to enable the preview.
                    </p>
                  ) : null}
                </div>
              </div>

              {preview ? (
                <div
                  className={styles.previewBox}
                  data-testid="margaux-preview"
                >
                  <div>
                    <div className={styles.previewLabel}>Intro</div>
                    <p className={preview.intro ? "" : styles.previewEmpty}>
                      {preview.intro ?? "Margaux didn't generate an intro."}
                    </p>
                  </div>
                  <div>
                    <div className={styles.previewLabel}>Ice-breaker</div>
                    <p
                      className={preview.iceBreaker ? "" : styles.previewEmpty}
                    >
                      {preview.iceBreaker ??
                        "Margaux didn't generate an ice-breaker."}
                    </p>
                  </div>
                  <div>
                    <div className={styles.previewLabel}>Hook line</div>
                    <p className={preview.hookLine ? "" : styles.previewEmpty}>
                      {preview.hookLine ??
                        "Margaux didn't generate a hook line."}
                    </p>
                  </div>
                </div>
              ) : null}
            </section>

            <section
              className={styles.section}
              id="segments"
              aria-labelledby="segments-heading"
            >
              <div className={styles.sectionHead}>
                <span className={styles.sectionLabel}>Step 5</span>
                <h2 id="segments-heading" className={styles.sectionTitle}>
                  Segments
                </h2>
              </div>
              <p className={styles.helperText}>
                Override the auto-suggested 4-week segments. Leave the row blank
                to fall back to Margaux's suggestions on submit.
              </p>
              <div className={styles.segmentList}>
                {segments.map((seg, i) => (
                  <div key={i} className={styles.segmentRow}>
                    <span className={styles.segmentNumber}>W{i + 1}</span>
                    <input
                      aria-label={`Segment ${i + 1} title`}
                      className={styles.smallInput}
                      placeholder={`Week ${i + 1} title`}
                      value={seg.title}
                      onChange={(e) =>
                        updateSegment(i, { title: e.target.value })
                      }
                    />
                    <input
                      aria-label={`Segment ${i + 1} description`}
                      className={styles.smallInput}
                      placeholder="Description (optional)"
                      value={seg.description}
                      onChange={(e) =>
                        updateSegment(i, { description: e.target.value })
                      }
                    />
                    <input
                      aria-label={`Segment ${i + 1} start page`}
                      className={styles.smallInput}
                      type="number"
                      min={1}
                      placeholder="Start"
                      value={seg.startPage}
                      onChange={(e) =>
                        updateSegment(i, { startPage: e.target.value })
                      }
                    />
                    <input
                      aria-label={`Segment ${i + 1} end page`}
                      className={styles.smallInput}
                      type="number"
                      min={1}
                      placeholder="End"
                      value={seg.endPage}
                      onChange={(e) =>
                        updateSegment(i, { endPage: e.target.value })
                      }
                    />
                  </div>
                ))}
              </div>
            </section>

            <div className={styles.foot}>
              <Link
                to="/staff/plotlounge-rooms"
                className={styles.btn}
                aria-label="Cancel and return to rooms list"
              >
                Cancel
              </Link>
              <button
                type="submit"
                className={`${styles.btn} ${styles.btnPrimary}`}
                disabled={!canSubmit}
              >
                {createRoom.isPending ? "Creating…" : "Create room"}
              </button>
            </div>
          </form>
        ) : null}
      </div>
    </main>
  );
}

export const plotloungeRoomNewRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/plotlounge-rooms/new",
  component: PlotloungeRoomNew,
});
